import { useCallback } from 'react';
import { useRelayEnvironment, type GraphQLTaggedNode } from 'react-relay';
import { useApolloClient } from '@apollo/react-hooks';

import { isRelayEnabled } from './isRelayEnabled.ts';
import {
	readFragmentWithCacheBridgeFallback,
	type FragmentCacheBridgeSource,
} from './cacheBridge/fragmentCacheBridgeSync.ts';
import { readFragmentShim } from './fragmentCacheOperations.ts';
import { toDocumentNode } from './toDocumentNode.ts';

export type ReadFragmentInteropOptions<TVariables extends Record<string, any>> = {
	id?: string;
	fragment: GraphQLTaggedNode;
	variables?: TVariables;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseReadFragmentInteropArgs = {
	id?: string;
	fragmentSpec: GraphQLTaggedNode;
	variables?: Record<string, any>;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export type UseReadFragmentInteropResult<TData> = (params?: {
	id?: string;
	variables?: Record<string, any>;
}) => TData | null;

export type UseReadFragmentInteropHook<TData> = (
	params: UseReadFragmentInteropArgs,
) => UseReadFragmentInteropResult<TData>;

/**
 * Imperative read fragment with the same options shape as Apollo's readFragment.
 * Uses BUILD_VARIANT to call either the Relay fragment shim or Apollo client readFragment.
 */
export function readFragmentInterop<TData, TVariables extends Record<string, any> = {}>(
	options: ReadFragmentInteropOptions<TVariables>,
): TData | null {
	const { id, fragment, variables, interopApolloAggClient, __callerPackage__ } = options;
	if (!id) {
		return null;
	}

	const source: FragmentCacheBridgeSource = isRelayEnabled(__callerPackage__) ? 'relay' : 'apollo';
	const readOriginal = () => {
		if (source === 'relay') {
			const env = window['GLOBAL_RELAY_ENV']!;
			return readFragmentShim<TData>(env, fragment, id, (variables ?? {}) as Record<string, any>);
		}

		const client = interopApolloAggClient
			? window['GLOBAL_APOLLO_AGG_CLIENT']
			: window['GLOBAL_APOLLO_CLIENT'];
		const data = client!.readFragment<TData>({
			id,
			fragment: toDocumentNode(fragment),
			variables: variables ?? {},
		});

		return data as TData;
	};

	// Read from the selected cache first; the bridge only falls back to the
	// secondary cache when the original fragment read misses.
	return readFragmentWithCacheBridgeFallback<TData>({
		source,
		fragment,
		fragmentId: id,
		variables: variables ?? {},
		useAggClient: interopApolloAggClient,
		readOriginal,
	});
}

function useReadFragmentInteropRelay<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec, id: defaultId, variables: defaultVariables } = params;
	const env = useRelayEnvironment();

	return useCallback(
		(callbackParams?: { id?: string; variables?: Record<string, any> }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const variables = callbackParams?.variables ?? defaultVariables ?? {};
			const readOriginal = () => readFragmentShim<TData>(env, fragmentSpec, id, variables);

			// Keep Relay as the original read path; cache bridge fallback is only
			// consulted if that read cannot satisfy the fragment.
			return readFragmentWithCacheBridgeFallback<TData>({
				source: 'relay',
				fragment: fragmentSpec,
				fragmentId: id,
				variables,
				relayEnvironment: env,
				readOriginal,
			});
		},
		[env, fragmentSpec, defaultId, defaultVariables],
	);
}

function useReadFragmentInteropApollo<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	const { fragmentSpec: fragment, id: defaultId, variables: defaultVariables } = params;

	const client = useApolloClient();

	return useCallback(
		(callbackParams?: { id?: string; variables?: Record<string, any> }) => {
			const id = callbackParams?.id ?? defaultId;
			if (!id) {
				return null;
			}
			const variables = callbackParams?.variables ?? defaultVariables ?? {};
			const readOriginal = () => {
				const data = client.readFragment<TData>({
					id,
					fragment: toDocumentNode(fragment),
					variables,
				});

				return data as TData;
			};

			// Keep Apollo as the original read path; cache bridge fallback is only
			// consulted if that read cannot satisfy the fragment.
			return readFragmentWithCacheBridgeFallback<TData>({
				source: 'apollo',
				fragment,
				fragmentId: id,
				variables,
				apolloClient: client,
				readOriginal,
			});
		},
		[client, fragment, defaultId, defaultVariables],
	);
}

export function useReadFragmentInterop<TData>(
	params: UseReadFragmentInteropArgs,
): UseReadFragmentInteropResult<TData> {
	return isRelayEnabled(params.__callerPackage__)
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropRelay(params)
		: // eslint-disable-next-line react-hooks/rules-of-hooks
			useReadFragmentInteropApollo(params);
}
